import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adminService } from '../../services/admin.service';
import LoadingSpinner from '../../components/LoadingSpinner';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadCampaign = async () => {
      try {
        const data = await adminService.getCampaign(id);
        if (isMounted) {
          setCampaign(data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    loadCampaign();

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage>Error: {error}</ErrorMessage>;
  if (!campaign) return <ErrorMessage>No se encontró la campaña</ErrorMessage>;

  return (
    <Container>
      <Header>
        <Title>{campaign.name}</Title>
        <BackButton onClick={() => navigate('/pmpl/admin/dashboard')}>
          Volver
        </BackButton>
      </Header>

      <Section>
        <SectionTitle>Información General</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <Label>Estado:</Label>
            <Value active={campaign.isActive}>
              {campaign.isActive ? 'Activa' : 'Inactiva'}
            </Value>
          </InfoItem>
          <InfoItem>
            <Label>Fecha inicio:</Label>
            <Value>{new Date(campaign.startDate).toLocaleDateString()}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Fecha fin:</Label>
            <Value>{new Date(campaign.endDate).toLocaleDateString()}</Value>
          </InfoItem>
          <InfoItem>
            <Label>URL pública:</Label>
            <Value>{campaign.slug}</Value>
          </InfoItem>
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>Premios</SectionTitle>
        <PrizesGrid>
          {campaign.prizes.map((prize, index) => (
            <PrizeCard key={index}>
              <PrizeName>{prize.name}</PrizeName>
              <PrizeStats>
                <Stat>
                  <StatLabel>Total:</StatLabel>
                  <StatValue>{prize.quantity}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Disponibles:</StatLabel>
                  <StatValue>{prize.remaining}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Probabilidad:</StatLabel>
                  <StatValue>{prize.probability}%</StatValue>
                </Stat>
              </PrizeStats>
            </PrizeCard>
          ))}
        </PrizesGrid>
      </Section>
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.2);
  color: #ffd700;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
  }
`;

const Section = styled.section`
  background: #2a2a2a;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #ffd700;
  font-size: 1.25rem;
  margin: 0 0 1.5rem 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.span`
  color: #999;
  font-size: 0.9rem;
`;

const Value = styled.span`
  color: ${props => props.active ? '#34D399' : '#fff'};
  font-size: 1.1rem;
`;

const PrizesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
`;

const PrizeCard = styled.div`
  background: #333;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 215, 0, 0.1);
`;

const PrizeName = styled.h3`
  color: #ffd700;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
`;

const PrizeStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const Stat = styled.div`
  text-align: center;
`;

const StatLabel = styled.div`
  color: #999;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  text-align: center;
  padding: 2rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  margin: 2rem;
`;

export default CampaignDetail; 